/**
 * Loss Runs tab — the editable claims table (OR-1174).
 *
 * Column groups:
 *   1. Identity (sticky left): #, Claimant / Claim #
 *   2. Context: Operator, Location, Venue, Description of Loss
 *   3. Dates: Incident Date, Report Date
 *   4. Amounts: Paid/Reserved Indemnity & Expense,
 *      Total Paid & Reserved (backend `total_incurred`)
 *   5. Policy: Status, Carrier, Valued,
 *      SIR by Community (backend `sir`), Loss Excess the Retention,
 *      Accident Year, Report Year
 *   6. Actions (sticky right): the ⋮ menu — Delete claim (OR-1431)
 *
 * Data source: the shared staged SoT (`useLossRuns(submissionId)`).
 * Every cell edit stages a mutation; the page's Save & Continue flushes
 * it. Calculated columns: Total Paid & Reserved (sum of the four
 * paid/reserved amounts) and Loss Excess the Retention (= max(0,
 * total − sir)) are FE-computed and update LIVE as the user types
 * (Henry, jun-23) — the backend doesn't recompute `total_incurred` on
 * update. SIR by Community stays backend-owned (`sir`). Accident/Report
 * Year = YEAR of the row's Incident/Report Date. Footer totals sum the
 * per-row FE figures over every row.
 *
 * PERF (OR — round-2 user testing): each row is a `React.memo`
 * (`LossRunRow`) fed referentially-stable callbacks/items, so a keystroke
 * re-renders ONLY the edited row instead of the whole table. The footer
 * totals + summary cards still update live (the parent re-renders), but
 * the untouched rows bail out — keeps typing fluid on large lists.
 *
 * NOTE: column sorting (the chevrons in the Figma) is intentionally
 * deferred — sorting a staged-editing table reorders the SoT and is
 * out of scope for OR-1174.
 */
import { memo, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { ArrowDown, ArrowUp, ChevronSelectorVertical, LineChartUp03 } from "@untitledui/icons";
import { SearchSelect, Select, Table, cx } from "@tricura/atlas-ui";
import { EditableCellProvider, EditableDateCell, EditableTextCell, InvalidShell } from "../../../../components/editable-cell";
import { RowDeleteMenu } from "../../../../components/row-actions-menu";
import { ARCHIVED_ROW_VISUAL } from "../../../../components/facility-archive-row";
import { findFacilityForLocationRef, isArchivedLocationRef } from "../../../../domain/facility-archive";
import { isArchivedClaim, isExcludedFromLossRunTotals, noteStatusBeforeArchive, statusToRestore } from "../claim-archive";
import { LocationScheduleWarning } from "../../../../components/location-schedule-warning";
import { StickyColDivider } from "../../../../components/sticky-col-divider";
import { TableEmptyState } from "../../../../components/table-empty-state";
import { useDeferredSetter } from "../../../../hooks/use-deferred-setter";
import { useFacilities } from "../../../../hooks/use-facilities";
import { useStates } from "../../../../hooks/use-states";
import { useLossRuns } from "../../../../hooks/use-loss-runs";
import type { Facility } from "../../../../types/facility";
import { LOSS_RUN_STATUSES, type LossRunRow, type LossRunStagePayload, type LossRunStatus } from "../../../../types/loss-run";
import { formatMoneyMask, moneyDigits } from "../../../../utils/money";
import { useSubmissionContext } from "../../context";
import { computeTotalIncurred, displayMoney, lossExcessOf, num, yearOf } from "../loss-run-calc";
import { validateLossRunRow } from "../loss-run-validation";

const STATUS_ITEMS = LOSS_RUN_STATUSES.map((s) => ({ id: s, label: s }));

/** Fixed column widths (px) — the table scrolls horizontally. */
const W = {
    rowNum: 56,
    claimant: 200,
    claimNumber: 170,
    operator: 200,
    location: 160,
    venue: 160,
    description: 280,
    // Date columns widened (OR-1370) so the MM/DD/YYYY value + picker sit
    // comfortably without crowding.
    incidentDate: 180,
    reportDate: 180,
    paidIndemnity: 160,
    paidExpense: 160,
    reservedIndemnity: 170,
    reservedExpense: 170,
    totalPaidReserved: 180,
    status: 150,
    carrier: 150,
    valued: 180,
    sirByCommunity: 170,
    lossExcess: 190,
    accidentYear: 140,
    reportYear: 140,
    actions: 64,
} as const;

const fixed = (px: number) => ({ width: px, minWidth: px });
// Content-driven width: the column grows to fit its widest item (all fields
// stay uniform since it's one column) but never past `max`; longer names
// truncate. Used for Location (min 160, max 560, per Andressa).
const hug = (min: number, max: number) => ({ minWidth: min, maxWidth: max });
const LOCATION_MAX = 560;

// Mobile (`<md`): drop the sticky pinning so the table scrolls as one block.
// Desktop (`md+`): the identity columns (#, Claimant, Claim #) stay frozen
// left with a divider after Claim #; the actions column stays pinned right.
// `max-md:static!` cancels the inline `left` offset on mobile: the cells are
// `position: relative` (atlas base), so without this the left-freeze offset
// still shifts them on mobile (where they're not sticky), leaving gaps and
// overlapping content. Static drops the offset so columns flow + scroll clean.
const STICKY_HEAD = "md:sticky md:z-10 md:bg-secondary max-md:static!";
const STICKY_CELL = "md:sticky md:z-10 md:bg-primary max-md:static!";
const STICKY_RIGHT_HEAD = "md:sticky md:right-0 md:z-10 md:bg-secondary md:shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.08)]";
const STICKY_RIGHT_CELL = "md:sticky md:right-0 md:z-10 md:bg-primary md:shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.08)]";
const READONLY_CELL = "text-secondary text-sm tabular-nums";

const venueCode = (raw: string) => raw.replace(/[^a-zA-Z]/g, "").toUpperCase().slice(0, 2);

function normalizeStatus(key: unknown): LossRunStatus {
    return LOSS_RUN_STATUSES.includes(key as Exclude<LossRunStatus, "" | "Archived">) ? (key as LossRunStatus) : "";
}

/** Total Paid & Reserved for one row — FE-derived (the backend doesn't
 *  recompute `total_incurred` on update). */
const rowTotalOf = (r: LossRunRow) =>
    computeTotalIncurred(r.paidIndemnity, r.paidExpense, r.reservedIndemnity, r.reservedExpense);

/** Stable empty fallback so a not-yet-ready facilities query doesn't hand
 *  the memoized rows a fresh `[]` on every render. */
const EMPTY_FACILITIES: Facility[] = [];
/** Stable empty fallback so the carrier sort keeps a stable input while the
 *  loss-runs query is still loading. */
const EMPTY_ROWS: LossRunRow[] = [];

/** Carrier comparator for the client-side sort — empties always sink to the
 *  bottom regardless of direction; named carriers compare case-insensitively. */
type SortDir = "asc" | "desc";

function byCarrier(a: LossRunRow, b: LossRunRow, dir: SortDir): number {
    if (!a.carrier && !b.carrier) return 0;
    if (!a.carrier) return 1;
    if (!b.carrier) return -1;
    const cmp = a.carrier.localeCompare(b.carrier, undefined, { sensitivity: "base" });
    return dir === "asc" ? cmp : -cmp;
}

/** Sortable column header — matches the app's other tables (Regulatory,
 *  Submissions, Heat Map): ChevronSelectorVertical when unsorted, ArrowUp/
 *  ArrowDown when active. Cycles none → asc → desc → none. */
function SortHeader({ label, dir, onToggle }: { label: string; dir: SortDir | null; onToggle: () => void }) {
    const Icon = dir ? (dir === "asc" ? ArrowUp : ArrowDown) : ChevronSelectorVertical;
    const ariaSort = !dir ? "none" : dir === "asc" ? "ascending" : "descending";
    return (
        <button
            type="button"
            onClick={onToggle}
            aria-sort={ariaSort}
            className="text-quaternary inline-flex cursor-pointer items-center gap-1.5 text-xs font-semibold whitespace-nowrap outline-none focus-visible:rounded focus-visible:ring-2 focus-visible:ring-focus-ring"
        >
            {label}
            <Icon aria-hidden="true" className={cx("size-3.5 shrink-0", dir ? "text-fg-secondary" : "text-fg-quaternary")} />
        </button>
    );
}

interface LossRunsTableProps {
    submissionId: string | undefined;
    /** Set once Save & Continue is attempted — flips cell error UI on. */
    showErrors: boolean;
}

export function LossRunsTable({ submissionId, showErrors }: LossRunsTableProps) {
    const { state, create, update, remove } = useLossRuns(submissionId);
    const rows = useMemo(() => (state.status === "ready" ? state.data : EMPTY_ROWS), [state]);
    const navigate = useNavigate();

    // Carrier-driven sort (design: Débora/Linda/Andressa — emphasize Carrier).
    // Client-side: the table is editable + fully loaded and the endpoint has no
    // sort param. Defaults to Carrier ascending; only Carrier is sortable.
    const [carrierSort, setCarrierSort] = useState<SortDir | null>("asc");
    const cycleCarrierSort = useCallback(
        () => setCarrierSort((d) => (d === "asc" ? "desc" : d === "desc" ? null : "asc")),
        [],
    );
    const sortedRows = useMemo(() => {
        if (!carrierSort) return rows;
        return [...rows].sort((a, b) => byCarrier(a, b, carrierSort));
    }, [rows, carrierSort]);

    // Location is a dropdown of submission facilities (per Henry, Slack
    // 5/25). Facilities also drive the "not in schedule" warning.
    const { state: facilitiesState, update: updateFacility } = useFacilities(submissionId);
    // Memoized so the array keeps the same identity across keystrokes —
    // otherwise every row's SearchSelect would see "new" facilities/items
    // and the memoized rows could not bail out of re-rendering.
    const facilities = useMemo(
        () => (facilitiesState.status === "ready" ? facilitiesState.data : EMPTY_FACILITIES),
        [facilitiesState],
    );
    // Items are keyed by the location's id, not its name: schedule names
    // can be duplicated (OR-1409/OR-1417) and duplicate keys break the
    // collection (React drops/omits options) besides making the selection
    // ambiguous.
    const facilityItems = useMemo(
        () => facilities.filter((f) => f.name.trim() !== "").map((f) => ({ id: f.id, label: f.name })),
        [facilities],
    );

    // Venue is a US-state dropdown fed by the real /v2/states backend
    // (no hardcoded list). Memoized so the row's SearchSelect keeps a
    // stable items identity.
    const statesQuery = useStates();
    const stateItems = useMemo(
        () => (statesQuery.status === "ready" ? statesQuery.data.map((s) => ({ id: s.code, label: s.code })) : []),
        [statesQuery],
    );

    const { detail } = useSubmissionContext();
    const detailId = detail.id;

    // No pagination (Andressa's Loom, jun-17): show every row, totals stay
    // pinned at the bottom for all tabs.

    /**
     * Claims on an archived facility, or archived themselves, are excluded
     * from the footer totals (OR-1431) — same combined predicate the cards
     * above this table use, so the two can't disagree (seen on dev before the
     * facility half of this landed: cards $25,000 vs footer $75,000). The rows
     * themselves stay visible, struck through, as history.
     */
    const countedRows = useMemo(
        () => rows.filter((r) => !isExcludedFromLossRunTotals(facilities, r)),
        [rows, facilities],
    );

    // Footer totals — FE-derived live figures (Henry, jun-23) summed over
    // every counted row. SIR by Community stays backend-owned
    // (`r.sirByCommunity`).
    // OR-1370: sum EVERY money column (not just Total Paid & Reserved / Loss
    // Excess) so each column footer carries its own total.
    const paidIndemnityAll = countedRows.reduce((sum, r) => sum + num(r.paidIndemnity), 0);
    const paidExpenseAll = countedRows.reduce((sum, r) => sum + num(r.paidExpense), 0);
    const reservedIndemnityAll = countedRows.reduce((sum, r) => sum + num(r.reservedIndemnity), 0);
    const reservedExpenseAll = countedRows.reduce((sum, r) => sum + num(r.reservedExpense), 0);
    const totalPaidReservedAll = countedRows.reduce((sum, r) => sum + rowTotalOf(r), 0);
    const lossExcessAll = countedRows.reduce((sum, r) => sum + lossExcessOf(rowTotalOf(r), r.sirByCommunity), 0);

    // ── Stable callbacks so each <LossRunRow> (React.memo) re-renders ONLY
    //    when its own row changes — a keystroke no longer re-renders the
    //    whole table. ──────────────────────────────────────────
    // `set` defers the SoT mutation off the keystroke (full-table re-render +
    // recalc run at low priority); each cell paints its local buffer instantly.
    const set = useDeferredSetter(update);
    const removeRow = useCallback((id: string) => void remove(id), [remove]);
    /** Archive / restore THIS claim, never the facility — a facility is a
     *  one-to-many relation over claims and it belongs to Schedule. Rides on
     *  `status` for want of a per-claim flag; see `claim-archive.ts`. */
    const setClaimArchived = useCallback(
        (row: LossRunRow, archive: boolean) =>
            set(row.id, { status: archive ? noteStatusBeforeArchive(row.id, row.status) : statusToRestore(row.id) }),
        [set],
    );
    /** Archive / restore the claim's FACILITY — a GLOBAL action, independent
     *  of `setClaimArchived` above, that cascades to Schedule/Regulatory/
     *  Operational/Legal via the shared `useFacilities` store. Stages via
     *  `update`, flushed by the page's Save button (see `loss-runs/index.tsx`). */
    const setFacilityArchived = useCallback(
        (facilityId: string, isExcluded: boolean) => void updateFacility(facilityId, { isExcluded }),
        [updateFacility],
    );
    const onCheckSchedule = useCallback(() => navigate(`/submissions/${detailId}/schedule`), [navigate, detailId]);
    const addRow = () => void create();

    return (
        <EditableCellProvider>
            <div className="relative flex flex-col overflow-hidden lg:min-h-0 lg:flex-1">
                <Table
                    aria-label="Loss Runs"
                    wrapperClassName="overflow-auto border-b border-secondary lg:min-h-0 lg:flex-1"
                    className="[&_tbody_tr:last-child]:border-b-0!"
                >
                    {/* Freeze the column header at the top of the scroll
                      * container so it stays visible while scrolling rows. */}
                    <Table.Header className="bg-secondary sticky top-0 z-20">
                        <Table.Head id="rowNum" label="#" isRowHeader className={STICKY_HEAD} style={{ ...fixed(W.rowNum), left: 0 }} />
                        {/* Identity columns (#, Claimant, Claim #) stay frozen left with a
                          * divider after Claim # — kept as before; only Carrier moved. */}
                        <Table.Head id="claimant" label="Claimant" className={STICKY_HEAD} style={{ ...fixed(W.claimant), left: W.rowNum }} />
                        <Table.Head id="claimNumber" label="Claim #" className={STICKY_HEAD} style={{ ...fixed(W.claimNumber), left: W.rowNum + W.claimant }}>
                            <StickyColDivider mdOnly />
                        </Table.Head>
                        {/* Carrier pulled to the front and sortable (project SortHeader
                          * pattern — ⇅ icon); the table defaults to sorting by it. */}
                        <Table.Head id="carrier" style={fixed(W.carrier)}>
                            <SortHeader label="Carrier" dir={carrierSort} onToggle={cycleCarrierSort} />
                        </Table.Head>
                        <Table.Head id="operator" label="Operator" style={fixed(W.operator)} />
                        <Table.Head id="location" label="Location" style={hug(W.location, LOCATION_MAX)} />
                        <Table.Head id="venue" label="Venue" style={fixed(W.venue)} />
                        {/* Tooltip icons hidden per Andressa (Loss Runs Figma):
                          * the definitions aren't ready yet, so the ⓘ glyphs are
                          * suppressed across all Loss Runs tables for now. */}
                        <Table.Head id="description" label="Description of Loss" style={fixed(W.description)} />
                        <Table.Head id="incidentDate" label="Incident Date" style={fixed(W.incidentDate)} />
                        <Table.Head id="reportDate" label="Report Date" style={fixed(W.reportDate)} />
                        <Table.Head id="paidIndemnity" label="Paid Indemnity" style={fixed(W.paidIndemnity)} />
                        <Table.Head id="paidExpense" label="Paid Expense" style={fixed(W.paidExpense)} />
                        <Table.Head id="reservedIndemnity" label="Reserved Indemnity" style={fixed(W.reservedIndemnity)} />
                        <Table.Head id="reservedExpense" label="Reserved Expense" style={fixed(W.reservedExpense)} />
                        <Table.Head id="totalPaidReserved" label="Total Paid & Reserved" style={fixed(W.totalPaidReserved)} />
                        <Table.Head id="status" label="Status" style={fixed(W.status)} />
                        <Table.Head id="valued" label="Valued" style={fixed(W.valued)} />
                        <Table.Head id="sirByCommunity" label="SIR by Community" style={fixed(W.sirByCommunity)} />
                        <Table.Head id="lossExcess" label="Loss Excess the Retention" style={fixed(W.lossExcess)} />
                        <Table.Head id="accidentYear" label="Accident Year" style={fixed(W.accidentYear)} />
                        <Table.Head id="reportYear" label="Report Year" style={fixed(W.reportYear)} />
                        <Table.Head id="actions" label="" className={STICKY_RIGHT_HEAD} style={fixed(W.actions)} />
                    </Table.Header>
                    <Table.Body>
                        {sortedRows.map((r, idx) => (
                            <LossRunRow
                                key={r.id}
                                row={r}
                                index={idx}
                                showErrors={showErrors}
                                facilities={facilities}
                                facilityItems={facilityItems}
                                stateItems={stateItems}
                                set={set}
                                removeRow={removeRow}
                                setClaimArchived={setClaimArchived}
                                setFacilityArchived={setFacilityArchived}
                                onCheckSchedule={onCheckSchedule}
                            />
                        ))}

                        {rows.length > 0 && (
                            <Table.Row id="__totals__" className="bg-secondary font-semibold sticky bottom-0 z-10">
                                <Table.Cell className="md:sticky md:z-10 md:bg-secondary max-md:static!" style={{ ...fixed(W.rowNum), left: 0 }} />
                                <Table.Cell
                                    className="md:sticky md:z-10 md:bg-secondary text-quaternary text-xs uppercase tracking-wider max-md:static!"
                                    style={{ ...fixed(W.claimant), left: W.rowNum }}
                                >
                                    Total
                                </Table.Cell>
                                <Table.Cell className="md:sticky md:z-10 md:bg-secondary max-md:static!" style={{ ...fixed(W.claimNumber), left: W.rowNum + W.claimant }}>
                                    <StickyColDivider mdOnly />
                                </Table.Cell>
                                <Table.Cell style={fixed(W.carrier)}/>
                                <Table.Cell style={fixed(W.operator)} />
                                <Table.Cell style={hug(W.location, LOCATION_MAX)} />
                                <Table.Cell style={fixed(W.venue)} />
                                <Table.Cell style={fixed(W.description)} />
                                <Table.Cell style={fixed(W.incidentDate)} />
                                <Table.Cell style={fixed(W.reportDate)} />
                                <Table.Cell style={fixed(W.paidIndemnity)}>
                                    <span className="text-primary text-sm tabular-nums" data-autotest="loss-runs-tab-total-paid-indemnity">
                                        {displayMoney(paidIndemnityAll)}
                                    </span>
                                </Table.Cell>
                                <Table.Cell style={fixed(W.paidExpense)}>
                                    <span className="text-primary text-sm tabular-nums" data-autotest="loss-runs-tab-total-paid-expense">
                                        {displayMoney(paidExpenseAll)}
                                    </span>
                                </Table.Cell>
                                <Table.Cell style={fixed(W.reservedIndemnity)}>
                                    <span className="text-primary text-sm tabular-nums" data-autotest="loss-runs-tab-total-reserved-indemnity">
                                        {displayMoney(reservedIndemnityAll)}
                                    </span>
                                </Table.Cell>
                                <Table.Cell style={fixed(W.reservedExpense)}>
                                    <span className="text-primary text-sm tabular-nums" data-autotest="loss-runs-tab-total-reserved-expense">
                                        {displayMoney(reservedExpenseAll)}
                                    </span>
                                </Table.Cell>
                                <Table.Cell style={fixed(W.totalPaidReserved)}>
                                    <span className="text-primary text-sm tabular-nums" data-autotest="loss-runs-tab-total-paid-reserved">
                                        {displayMoney(totalPaidReservedAll)}
                                    </span>
                                </Table.Cell>
                                <Table.Cell style={fixed(W.status)} />
                                <Table.Cell style={fixed(W.valued)} />
                                <Table.Cell style={fixed(W.sirByCommunity)} />
                                <Table.Cell style={fixed(W.lossExcess)}>
                                    <span className="text-primary text-sm tabular-nums" data-autotest="loss-runs-tab-total-loss-excess">
                                        {displayMoney(lossExcessAll)}
                                    </span>
                                </Table.Cell>
                                <Table.Cell style={fixed(W.accidentYear)} />
                                <Table.Cell style={fixed(W.reportYear)} />
                                <Table.Cell className="md:sticky md:right-0 md:z-10 md:bg-secondary md:shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.08)]" style={fixed(W.actions)} />
                            </Table.Row>
                        )}
                    </Table.Body>
                </Table>

                {rows.length === 0 && (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                        <div className="pointer-events-auto">
                            <TableEmptyState
                                icon={LineChartUp03}
                                title="Add Loss Runs"
                                description="Use the button below to start building the Loss Runs"
                                onAdd={addRow}
                                data-autotest="loss-runs-table-empty-state-add-button"
                            />
                        </div>
                    </div>
                )}
            </div>
        </EditableCellProvider>
    );
}

interface LossRunRowProps {
    row: LossRunRow;
    index: number;
    showErrors: boolean;
    facilities: Facility[];
    facilityItems: { id: string; label: string }[];
    stateItems: { id: string; label: string }[];
    set: (id: string, patch: LossRunStagePayload) => void;
    removeRow: (id: string) => void;
    setClaimArchived: (row: LossRunRow, archive: boolean) => void;
    setFacilityArchived: (facilityId: string, isExcluded: boolean) => void;
    onCheckSchedule: () => void;
}

/**
 * One claims row. `React.memo` so a keystroke in another row doesn't
 * re-render this one — the parent feeds referentially-stable props
 * (`set`/`removeRow`/`onCheckSchedule`/`facilityItems`) and the row
 * object itself only gets a new identity when ITS data changes (the SoT
 * `map` preserves untouched rows). Per-row validation + the live Total
 * are computed here so they only run for the row that actually changed.
 */
const LossRunRow = memo(function LossRunRow({
    row: r,
    index,
    showErrors,
    facilities,
    facilityItems,
    stateItems,
    set,
    removeRow,
    setClaimArchived,
    setFacilityArchived,
    onCheckSchedule,
}: LossRunRowProps) {
    // Live validation: lenient by default (only flags fully-typed invalid
    // values, no required checks). After a failed save the page sets
    // `showErrors=true` and we flip to strict.
    const errors = validateLossRunRow(r, { requireAll: showErrors });
    const total = rowTotalOf(r);
    // A row's location is "in schedule" when it's blank or matches a
    // facility name. A non-blank value with no match drives the inline
    // warning (Loss Runs Figma).
    const loc = r.location.trim();
    const inSchedule = loc === "" || facilities.some((f) => f.name.trim() === loc);
    // An archived facility's claims still exist, but the backend drops them
    // from every loss-run total and from the per-year breakdown (OR-1431), so
    // the row is dimmed + struck to match — and the footer leaves it out of the
    // column totals. Visual only: the row is a CLAIM, not the facility, so its
    // own data stays editable and deletable.
    // Two independent reasons a row reads as excluded. The facility one is the
    // backend's: it already drops those claims from every loss-run total, so the
    // footer leaves them out too. The claim one is this row's own archive flag —
    // struck through, and now ALSO left out of the FE-derived cards/footer
    // (`isExcludedFromLossRunTotals`), even though nothing server-side knows
    // about it (see `claim-archive.ts`) — so the backend-derived Loss Ratio card
    // and generated documents still count it.
    const facilityArchived = isArchivedLocationRef(facilities, r);
    const claimArchived = isArchivedClaim(r);
    const archived = facilityArchived || claimArchived;
    // Resolved once for the ⋮ menu's facility Archive/Restore item — omitted
    // below when the row's location doesn't resolve to a facility.
    const rowFacility = findFacilityForLocationRef(facilities, r);
    return (
        <Table.Row id={r.id} className={archived ? ARCHIVED_ROW_VISUAL : undefined}>
            <Table.Cell className={`${STICKY_CELL} text-tertiary`} style={{ ...fixed(W.rowNum), left: 0 }}>
                {index + 1}
            </Table.Cell>
            <Table.Cell className={STICKY_CELL} style={{ ...fixed(W.claimant), left: W.rowNum }}>
                <EditableTextCell
                    ariaLabel="Claimant Name"
                    placeholder="Enter Claimant Name"
                    value={r.claimantName}
                    onChange={(v) => set(r.id, { claimantName: v })}
                    isInvalid={!!errors.claimantName}
                    errorMessage={errors.claimantName}
                    data-autotest="loss-runs-tab-claimant-name-input"
                />
            </Table.Cell>
            <Table.Cell className={STICKY_CELL} style={{ ...fixed(W.claimNumber), left: W.rowNum + W.claimant }}>
                <EditableTextCell
                    ariaLabel="Claim Number"
                    placeholder="Claim #"
                    value={r.claimNumber}
                    onChange={(v) => set(r.id, { claimNumber: v })}
                    data-autotest="loss-runs-tab-claim-number-input"
                />
                <StickyColDivider mdOnly />
            </Table.Cell>
            <Table.Cell style={fixed(W.carrier)}>
                <EditableTextCell
                    ariaLabel="Carrier"
                    placeholder="Carrier"
                    value={r.carrier}
                    onChange={(v) => set(r.id, { carrier: v })}
                    data-autotest="loss-runs-tab-carrier-input"
                />
            </Table.Cell>
            <Table.Cell style={fixed(W.operator)}>
                {/* Read-only (OR-1370/OR-1371): the backend derives operator
                  * from the linked location (schedule) via location_id and
                  * ignores it on write. Edit it by changing the Location. */}
                <span className={READONLY_CELL} data-autotest="loss-runs-tab-operator-input">
                    {r.operator || "—"}
                </span>
            </Table.Cell>
            <Table.Cell style={hug(W.location, LOCATION_MAX)}>
                {/* When the location isn't in the policy Schedule, the
                  * cell becomes a dropdown-styled warning (round glyph
                  * + value); clicking it shows the message + a jump to
                  * Schedule (Andressa review). Otherwise, the normal
                  * facility picker. */}
                {inSchedule ? (
                    <InvalidShell isInvalid={false} reserveSpace={false}>
                        <SearchSelect
                            size="sm"
                            aria-label="Location"
                            placeholder="—"
                            wrapValue
                            // Selection is id-based (OR-1409); the name fallback
                            // covers rows staged before locationId existed.
                            selectedKey={r.locationId || facilities.find((f) => f.name === r.location)?.id || null}
                            onSelectionChange={(k) => {
                                // Picking a Location also auto-fills Venue with
                                // that facility's State, and mirrors the linked
                                // facility's Operator (both from the facilities
                                // SoT). Operator is read-only/server-derived
                                // (OR-1370/OR-1371), so we refresh it optimistically
                                // here — otherwise it stays stale until save+reload
                                // (OR-1411). The key IS the facility id, so the
                                // lookup is exact even with duplicated names.
                                const fac = k == null ? undefined : facilities.find((f) => f.id === k);
                                set(
                                    r.id,
                                    fac
                                        ? { location: fac.name, locationId: fac.id, operator: fac.operator, ...(fac.state ? { venue: venueCode(fac.state) } : {}) }
                                        : { location: "", locationId: "", operator: "" },
                                );
                            }}
                            items={facilityItems}
                            data-autotest="loss-runs-tab-location-select"
                        >
                            {(item) => (
                                <SearchSelect.Item id={item.id} wrapLabel data-autotest="loss-runs-tab-location-select-item">
                                    {item.label}
                                </SearchSelect.Item>
                            )}
                        </SearchSelect>
                    </InvalidShell>
                ) : (
                    <LocationScheduleWarning location={r.location} onCheckSchedule={onCheckSchedule} />
                )}
            </Table.Cell>
            <Table.Cell style={fixed(W.venue)}>
                {/* Venue is a US-state dropdown (real /v2/states). Picking a
                  * Location auto-fills it from that facility's State. */}
                <InvalidShell isInvalid={false} reserveSpace={false}>
                    <SearchSelect
                        size="sm"
                        aria-label="Venue"
                        placeholder="—"
                        selectedKey={r.venue || null}
                        onSelectionChange={(k) => set(r.id, { venue: (k as string) ?? "" })}
                        items={stateItems}
                        popoverWidthMul={1.2}
                        data-autotest="loss-runs-tab-venue-select"
                    >
                        {(item) => <SearchSelect.Item id={item.id} data-autotest={`loss-runs-tab-venue-select-item-${item.label}`}>
                            {item.label}
                        </SearchSelect.Item>}
                    </SearchSelect>
                </InvalidShell>
            </Table.Cell>
            <Table.Cell style={fixed(W.description)}>
                <EditableTextCell
                    ariaLabel="Description of Loss"
                    placeholder="Description of Loss"
                    value={r.descriptionOfLoss}
                    onChange={(v) => set(r.id, { descriptionOfLoss: v })}
                    maxLength={500}
                    data-autotest="loss-runs-tab-description-input"
                />
            </Table.Cell>
            <Table.Cell style={fixed(W.incidentDate)}>
                <EditableDateCell
                    ariaLabel="Incident Date"
                    value={r.incidentDate}
                    onChange={(v) => set(r.id, { incidentDate: v })}
                    isInvalid={!!errors.incidentDate}
                    errorMessage={errors.incidentDate}
                    data-autotest="loss-runs-tab-incident-date-input"
                />
            </Table.Cell>
            <Table.Cell style={fixed(W.reportDate)}>
                <EditableDateCell
                    ariaLabel="Report Date"
                    value={r.reportDate}
                    onChange={(v) => set(r.id, { reportDate: v })}
                    isInvalid={!!errors.reportDate}
                    errorMessage={errors.reportDate}
                    data-autotest="loss-runs-tab-report-date-input"
                />
            </Table.Cell>
            <Table.Cell style={fixed(W.paidIndemnity)}>
                <EditableTextCell
                    ariaLabel="Paid Indemnity"
                    placeholder="$0.00"
                    value={r.paidIndemnity}
                    onChange={(v) => set(r.id, { paidIndemnity: v })}
                    inputMode="decimal"
                    sanitize={moneyDigits}
                    format={formatMoneyMask}
                    data-autotest="loss-runs-tab-paid-indemnity-input"
                />
            </Table.Cell>
            <Table.Cell style={fixed(W.paidExpense)}>
                <EditableTextCell
                    ariaLabel="Paid Expense"
                    placeholder="$0.00"
                    value={r.paidExpense}
                    onChange={(v) => set(r.id, { paidExpense: v })}
                    inputMode="decimal"
                    sanitize={moneyDigits}
                    format={formatMoneyMask}
                    data-autotest="loss-runs-tab-paid-expense-input"
                />
            </Table.Cell>
            <Table.Cell style={fixed(W.reservedIndemnity)}>
                <EditableTextCell
                    ariaLabel="Reserved Indemnity"
                    placeholder="$0.00"
                    value={r.reservedIndemnity}
                    onChange={(v) => set(r.id, { reservedIndemnity: v })}
                    inputMode="decimal"
                    sanitize={moneyDigits}
                    format={formatMoneyMask}
                    data-autotest="loss-runs-tab-reserved-indemnity-input"
                />
            </Table.Cell>
            <Table.Cell style={fixed(W.reservedExpense)}>
                <EditableTextCell
                    ariaLabel="Reserved Expense"
                    placeholder="$0.00"
                    value={r.reservedExpense}
                    onChange={(v) => set(r.id, { reservedExpense: v })}
                    inputMode="decimal"
                    sanitize={moneyDigits}
                    format={formatMoneyMask}
                    data-autotest="loss-runs-tab-reserved-expense-input"
                />
            </Table.Cell>
            <Table.Cell style={fixed(W.totalPaidReserved)}>
                <span className={READONLY_CELL} data-autotest="loss-runs-tab-total-paid-reserved-input">
                    {displayMoney(total)}
                </span>
            </Table.Cell>
            <Table.Cell style={fixed(W.status)}>
                {/* `Archived` isn't one of the dropdown's options, so the Select
                  * would render blank on an archived claim. Show the value as
                  * read-only text instead — the same treatment every other
                  * non-editable cell in this table gets. */}
                {claimArchived ? (
                    <span className={READONLY_CELL}>Archived</span>
                ) : (
                    <InvalidShell isInvalid={false} reserveSpace={false}>
                        <Select
                            size="sm"
                            aria-label="Status"
                            placeholder="—"
                            selectedKey={r.status || null}
                            onSelectionChange={(k) => set(r.id, { status: normalizeStatus(k) })}
                            items={STATUS_ITEMS}
                            data-autotest="loss-runs-tab-status-select"
                        >
                            {(item) => <Select.Item id={item.id} data-autotest={`loss-runs-tab-status-select-item-${item.label}`}>
                                {item.label}
                            </Select.Item>}
                        </Select>
                    </InvalidShell>
                )}
            </Table.Cell>
            <Table.Cell style={fixed(W.valued)}>
                <EditableDateCell
                    ariaLabel="Valued"
                    value={r.valued}
                    onChange={(v) => set(r.id, { valued: v })}
                    isInvalid={!!errors.valued}
                    errorMessage={errors.valued}
                    data-autotest="loss-runs-tab-valued-input"
                />
            </Table.Cell>
            {/* SIR by Community is backend-owned (`r.sirByCommunity` — the
              * matched location's SIR, recomputed server-side when the
              * Schedule Retention or the row's Location changes; OR-1337).
              * Total Paid & Reserved and Loss Excess (= max(0, total − sir))
              * are FE-computed and update live as the user types (Henry,
              * jun-23). Accident/Report Year = YEAR(Incident/Report Date). */}
            <Table.Cell style={fixed(W.sirByCommunity)}>
                <span className={READONLY_CELL} data-autotest="loss-runs-tab-sir-by-community-input">
                    {displayMoney(num(r.sirByCommunity))}
                </span>
            </Table.Cell>
            <Table.Cell style={fixed(W.lossExcess)}>
                <span className={READONLY_CELL} data-autotest="loss-runs-tab-loss-excess-input">
                    {displayMoney(lossExcessOf(total, r.sirByCommunity))}
                </span>
            </Table.Cell>
            <Table.Cell style={fixed(W.accidentYear)}>
                <span className={READONLY_CELL} data-autotest="loss-runs-tab-accident-year-input">
                    {yearOf(r.incidentDate)}
                </span>
            </Table.Cell>
            <Table.Cell style={fixed(W.reportYear)}>
                <span className={READONLY_CELL} data-autotest="loss-runs-tab-report-year-input">
                    {yearOf(r.reportDate)}
                </span>
            </Table.Cell>
            {/* The ⋮ that replaced the trash. Delete and "Archive"/"Restore" act
              * on THIS claim. Once the claim's FACILITY is archived, that claim
              * toggle is replaced by "Restore Facility" instead (RowDeleteMenu
              * hides one or the other — never both): the row already renders
              * archived because of the facility, so a claim-only restore would
              * silently do nothing visible, and only restoring the facility
              * (a GLOBAL action, cascading to Schedule/Regulatory/Operational/
              * Legal) actually changes how the row looks. Omitted entirely when
              * the row's Location doesn't resolve to a facility. */}
            <Table.Cell className={STICKY_RIGHT_CELL} style={fixed(W.actions)}>
                <div className="flex justify-end">
                    <RowDeleteMenu
                        title="Delete claim"
                        description="Delete this claim from the submission. This action cannot be undone."
                        rowLabel={r.claimantName || r.claimNumber}
                        data-autotest="loss-runs-tab-actions-menu"
                        onDelete={() => removeRow(r.id)}
                        archive={{
                            isArchived: claimArchived,
                            onArchive: () => setClaimArchived(r, true),
                            onRestore: () => setClaimArchived(r, false),
                        }}
                        facilityArchive={
                            rowFacility
                                ? {
                                      isArchived: rowFacility.isExcluded,
                                      // The claim's own status can be archived independent of
                                      // the facility (see the note above `archived`). Restoring
                                      // the facility alone wouldn't clear that — the row would
                                      // stay struck through with no menu item left to fix it —
                                      // so also restore the claim when it's independently
                                      // archived.
                                      onRestore: () => {
                                          setFacilityArchived(rowFacility.id, false);
                                          if (claimArchived) setClaimArchived(r, false);
                                      },
                                      alsoRestoresRow: claimArchived,
                                  }
                                : undefined
                        }
                    />
                </div>
            </Table.Cell>
        </Table.Row>
    );
});
